import { useMemo } from 'react';
import { useGetFilesQuery, FileFilter } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';
import { formatBytes } from '@/lib/utils/format-bytes';

/** Transformed file for display in detail sheet lists */
export interface FileListItem {
  id: string;
  name: string;
  size: string;
  date: string;
  source: string;
  url?: string | null;
}

/** Transform raw GraphQL file node into a display-friendly object */
function transformFile(node: { id: string; filename?: string | null; size?: number | null; uploadedAt?: string | null; createdAt?: string | null; source?: string | null; fileUrl?: string | null }): FileListItem {
  return {
    id: node.id,
    name: node.filename || 'Unknown File',
    size: formatBytes(node.size),
    date: node.uploadedAt || node.createdAt || '',
    source: node.source || 'unknown',
    url: node.fileUrl,
  };
}

export interface UseFilesBaseOptions {
  /** The entity-specific filter fields (e.g. { contactId: '123' }) */
  entityFilter: Record<string, string>;
  /** Entity ID for skip condition */
  entityId: string;
  skip?: boolean;
  /** Dummy files data for preview mode */
  dummyFiles: FileListItem[];
}

/**
 * Shared hook for fetching files associated with any entity.
 * Supports Lovable Preview mode with dummy data.
 */
export function useFilesBase({
  entityFilter,
  entityId,
  skip = false,
  dummyFiles,
}: UseFilesBaseOptions) {
  const workspaceId = useWorkspaceId();

  const filter: FileFilter = useMemo(
    () => ({
      workspaceId: workspaceId || '',
      ...entityFilter,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceId, entityId]
  );

  const { data, loading, error, refetch } = useGetFilesQuery({
    variables: {
      filter,
      first: 100,
      sort: [{ field: 'properties.uploaded_at.value_datetime', sort: 'DESC' }, { field: '_id', sort: 'ASC' }],
    },
    skip: skip || isLovablePreview() || !workspaceId || !entityId,
    fetchPolicy: 'cache-and-network',
  });

  const files = useMemo(() => {
    if (isLovablePreview()) {
      return dummyFiles;
    }
    return data?.files?.edges?.map((edge) => transformFile(edge.node)) || [];
  }, [data, dummyFiles]);

  return {
    files,
    loading: isLovablePreview() ? false : loading,
    error: isLovablePreview() ? null : error,
    refetch: isLovablePreview() ? (() => Promise.resolve({} as ReturnType<typeof refetch>)) : refetch,
  };
}
